import { type ReactNode, useMemo, useState } from "react";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  ExpandableSection,
  Header,
  Link,
  Pagination,
  ProgressBar,
  Spinner,
  StatusIndicator,
  Table,
} from "@amzn/awsui-components-react";
import ErrorAlert from "../common/ErrorAlert";
import type {
  PsnsRedriveInfoResult,
  Stage,
} from "../../lusca-search-api/generated-src";
import {
  generateExecutionUrlFromStepFunction,
  generateMcmUrl,
  generateS3ObjectUrl,
  generateS3PrefixUrl,
} from "../../helpers/logUtils";
import { extractRedriveErrorMessage } from "../dlq/dlqRedriveError";

interface Props {
  combinedError: string | null;
  details: PsnsRedriveInfoResult | null;
  loadingDetails: boolean;
  onBack: () => void;
  executionArn: string;
  stage: Stage;
}

const PAGE_SIZE = 50;

function ValueWithLabel({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <div>{children}</div>
    </div>
  );
}

const statusType = (status?: string) =>
  status === "COMPLETED"
    ? "success"
    : status === "PENDING"
    ? "in-progress"
    : "error";

const eventStatusType = (status: string) =>
  status === "PUBLISHED" || status === "DRY_RUN"
    ? "success"
    : status === "SKIPPED"
    ? "stopped"
    : "error";

const renderEventStatus = (item: { status: string }) => (
  <StatusIndicator type={eventStatusType(item.status)}>
    {item.status}
  </StatusIndicator>
);

function PsnsRedriveResultsTable({
  details,
  loading,
  stage,
}: {
  details: PsnsRedriveInfoResult;
  loading: boolean;
  stage: Stage;
}) {
  const items = useMemo(
    () =>
      Object.entries(details.result?.publishedEvents ?? {}).map(
        ([eventId, status]) => ({ eventId, status: String(status) })
      ),
    [details.result?.publishedEvents]
  );
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const pageIndex = Math.min(currentPageIndex, totalPages);
  const pageItems = items.slice(
    (pageIndex - 1) * PAGE_SIZE,
    pageIndex * PAGE_SIZE
  );

  const resultsConsoleUrl = details.result?.resultsS3Uri
    ? generateS3PrefixUrl(stage, details.result.resultsS3Uri)
    : null;
  const truncatedDescription = details.result?.truncated ? (
    <>
      Showing the first 1000 results; this redrive processed more.{" "}
      {resultsConsoleUrl ? (
        <Link external href={resultsConsoleUrl}>
          View the full result files in S3
        </Link>
      ) : (
        "See the S3 result files for the full set."
      )}
    </>
  ) : undefined;

  return (
    <Table
      resizableColumns
      stickyHeader
      loading={loading && items.length === 0}
      items={pageItems}
      columnDefinitions={[
        {
          id: "eventId",
          header: "Event ID",
          cell: (item) => item.eventId,
        },
        {
          id: "status",
          header: "Status",
          cell: renderEventStatus,
        },
      ]}
      header={
        <Header
          variant="h2"
          counter={`(${items.length})`}
          description={truncatedDescription}
        >
          Event results
        </Header>
      }
      empty={<Box textAlign="center">No per-event results</Box>}
      pagination={
        <Pagination
          currentPageIndex={pageIndex}
          pagesCount={totalPages}
          onChange={({ detail }) =>
            setCurrentPageIndex(detail.currentPageIndex)
          }
        />
      }
    />
  );
}

export function PsnsRedriveExecutionView({
  combinedError,
  details,
  loadingDetails,
  onBack,
  executionArn,
  stage,
}: Props) {
  const fallback = !details && loadingDetails ? "Loading…" : "-";
  const metadata = details?.metadata;
  const counts = details?.result?.counts;
  // Live while running; failed items are finished too, so they count as processed.
  const progress = metadata?.progress;
  const processed = (progress?.succeeded ?? 0) + (progress?.failed ?? 0);
  const sourceUrl = metadata?.eventsS3Uri
    ? generateS3ObjectUrl(stage, metadata.eventsS3Uri)
    : null;

  return (
    <SpaceBetween size="l">
      {combinedError && (
        <ErrorAlert content={combinedError} header="PSNS Redrive Error" />
      )}
      <Button iconName="arrow-left" onClick={onBack} variant="link">
        Back to PSNS redrive
      </Button>
      <Container
        header={
          <Header
            variant="h2"
            actions={
              <Button
                iconName="external"
                iconAlign="right"
                onClick={() =>
                  window.open(
                    generateExecutionUrlFromStepFunction(stage, executionArn),
                    "_blank"
                  )
                }
              >
                View in Console
              </Button>
            }
          >
            PSNS Redrive Execution
          </Header>
        }
      >
        <ColumnLayout columns={3} variant="text-grid">
          <ValueWithLabel label="Stage">{stage}</ValueWithLabel>
          <ValueWithLabel label="Status">
            {details?.status ? (
              <StatusIndicator type={statusType(details.status)}>
                {details.status === "PENDING" ? "In progress…" : details.status}
              </StatusIndicator>
            ) : (
              fallback
            )}
          </ValueWithLabel>
          <ValueWithLabel label="Executor">
            {metadata?.executor || fallback}
          </ValueWithLabel>
          <ValueWithLabel label="Request Date">
            {metadata?.timestamp
              ? new Date(metadata.timestamp * 1000).toLocaleString()
              : fallback}
          </ValueWithLabel>
          <ValueWithLabel label="Type">
            {metadata?.requestType || fallback}
          </ValueWithLabel>
          <ValueWithLabel label="Source">
            {metadata?.eventsS3Uri ? (
              sourceUrl ? (
                <Link
                  href={sourceUrl}
                  external
                  externalIconAriaLabel="Opens in a new tab"
                >
                  {metadata.eventsS3Uri}
                </Link>
              ) : (
                metadata.eventsS3Uri
              )
            ) : (
              fallback
            )}
          </ValueWithLabel>
          <ValueWithLabel label="Events">
            {metadata?.eventCount ?? fallback}
          </ValueWithLabel>
          {metadata?.mcmId && (
            <ValueWithLabel label="MCM">
              <Link
                href={generateMcmUrl(stage, metadata.mcmId)}
                external
                externalIconAriaLabel="Opens in a new tab"
              >
                {metadata.mcmId}
              </Link>
            </ValueWithLabel>
          )}
        </ColumnLayout>
      </Container>
      {details?.status === "PENDING" && (
        <SpaceBetween size="s">
          {progress && progress.total ? (
            <ProgressBar
              value={Math.floor((processed / progress.total) * 100)}
              label={metadata?.dryRun ? "Dry run progress" : "Redrive progress"}
              description={`${processed.toLocaleString()} of ${progress.total.toLocaleString()} events processed${
                progress.running
                  ? ` · ${progress.running.toLocaleString()} in progress`
                  : ""
              }${
                progress.failed
                  ? ` · ${progress.failed.toLocaleString()} failed`
                  : ""
              }`}
              data-testid="psns-redrive-progress"
            />
          ) : null}
          <SpaceBetween size="xs" direction="horizontal" alignItems="center">
            <Spinner size="normal" data-testid="redrive-auto-refresh-spinner" />
            <Box variant="small" color="text-status-inactive">
              The redrive is running. This page updates automatically.
            </Box>
          </SpaceBetween>
        </SpaceBetween>
      )}
      {counts && counts.total ? (
        <Box data-testid="psns-redrive-counts">
          Of {counts.total} event(s): {counts.published ?? 0} published,{" "}
          {counts.invalid ?? 0} invalid, {counts.failed ?? 0} failed,{" "}
          {counts.pending ?? 0} never ran.
        </Box>
      ) : null}
      {details?.status === "ERROR" &&
        (() => {
          // The cause holds the readable message; result.error is just the
          // exception class. Fall back so we never hide an error.
          const friendly =
            extractRedriveErrorMessage(details.result?.cause) ||
            details.result?.error ||
            details.error ||
            "The redrive execution failed.";
          const rawDetail =
            details.result?.cause &&
            details.result.cause.trim() !== friendly.trim()
              ? details.result.cause
              : null;
          return (
            <Alert type="error" header="PSNS redrive failed">
              <SpaceBetween size="s">
                <Box>{friendly}</Box>
                {rawDetail && (
                  <ExpandableSection headerText="Raw error details">
                    <Box
                      variant="code"
                      fontSize="body-s"
                      color="text-status-error"
                    >
                      {rawDetail}
                    </Box>
                  </ExpandableSection>
                )}
              </SpaceBetween>
            </Alert>
          );
        })()}
      {details?.result && (
        <PsnsRedriveResultsTable
          details={details}
          loading={loadingDetails}
          stage={stage}
        />
      )}
    </SpaceBetween>
  );
}
